import * as d3 from 'd3';
import { TDrawModel } from '../../draw.model';
import {
  TCircleElements,
  TEntityActionCb,
  TEntityActions,
  TIconConfig,
  TRectElements,
  TStripElement,
  TTimelineModel,
} from '../../timeline.types';

const HIGHLIGHTED_STRIP = {
  BG_COLOR: '#FFEB3B',
  TEXT_COLOR: '#000',
  BORDER_COLOR: '#000',
};

const STRIP_ICON = {
  SIZE: 18,
  FONT_SIZE: 12,
  PADDING_LEFT: 5,
  GAP: 4,
  BORDER_RADIUS: 4,
  CHAR_WIDTH_RATIO: 0.7,
  DEFAULT_ICON_COLOR: '#0F31A9',
  GLYPH_VIEWBOX: 22,
};

const getIconWidth = (icon: TIconConfig) => {
  if (icon.text) {
    const iconTextWidth = icon.text.length * STRIP_ICON.FONT_SIZE * STRIP_ICON.CHAR_WIDTH_RATIO;
    return STRIP_ICON.PADDING_LEFT + iconTextWidth + STRIP_ICON.SIZE;
  }
  return STRIP_ICON.SIZE;
};

export const DRAW_ELEMENTS = {
  drawElements: (
    elements: d3.Selection<
      d3.BaseType | SVGSVGElement,
      TTimelineModel['entities'][number]['entities'][number]['elements'][number],
      d3.BaseType | SVGGElement,
      TTimelineModel['entities'][number]['entities'][number]
    >,
    drawModel: TDrawModel,
    stripEventCb: TEntityActionCb,
  ) => {
    // Clean up
    elements.selectAll('rect.elm-mask-background').remove();
    elements
      .filter((d) => d.type === 'STRIP' && d.mask)
      .selectAll('.elm-mask-background')
      .data((d) => [d] as Required<TStripElement>[])
      .join('rect')
      .classed('elm-mask-background', true)
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', '100%')
      .attr('height', '100%')
      .attr('font-weight', 'bold')
      .attr('fill', (d) => (d.mask ? 'black' : 'transparent'))
      .attr('rx', '4')
      .attr('ry', '4');

    // clean up
    elements.selectAll('rect.elm').remove();
    elements
      .filter((d) => d.type === 'STRIP' || d.type === 'MARKER' || d.type === 'MESH')
      .selectAll('.elm')
      .data((d) => [d] as TRectElements[])
      .join('rect')
      .classed('elm', true)
      .attr('fill', (d) => (d.isStripHighlighted && d.type === 'STRIP' ? HIGHLIGHTED_STRIP.BG_COLOR : d.color))
      .attr('stroke', (d) =>
        d.isStripHighlighted && d.type === 'STRIP' ? HIGHLIGHTED_STRIP.BORDER_COLOR : d.borderColor || '',
      )
      .attr('stroke-width', (d) => (d.borderColor || d.isStripHighlighted ? 1 : 0))
      .attr('width', '100%')
      .attr('height', (d) => {
        return (drawModel.layout.stackHeights[d.stackCount] * (d.heightInPercent ?? 100)) / 100;
      })
      .attr('mask', (d) => {
        switch (d.type) {
          case 'STRIP':
          case 'MARKER': {
            return d.mask ? `url(#${drawModel.defsIds['mask-zig-zag']})` : '';
          }
          case 'MESH': {
            return d.mask ? `url(#${drawModel.defsIds['mask-mesh']})` : '';
          }
        }
      })
      .attr('rx', '4')
      .attr('ry', '4');

    elements.selectAll('rect.elm-strip-pattern').remove();
    elements
      .filter((d) => d.type === 'STRIP' && !!d.progressFill)
      .selectAll('.elm-strip-pattern')
      .data((d) => [d] as TStripElement[])
      .join('rect')
      .classed('elm-strip-pattern', true)
      .attr('fill', (d) => `url(#${d.progressFill?.pattern || ''})`)
      .attr('width', (d) => `${d.progressFill?.fillInPercent || 0}%`)
      .attr('height', (d) => {
        return (drawModel.layout.stackHeights[d.stackCount] * (d.heightInPercent ?? 100)) / 100;
      })
      .attr('rx', '4')
      .attr('ry', '4');

    elements.selectAll('.elm-text-group').remove();
    const textGroup = elements
      .filter((d) => d.type === 'STRIP' && !!d.textList)
      .selectAll('.elm-text-group')
      .data((d) => [d] as TStripElement[])
      .join('g')
      .classed('elm-text-group', true);

    // Clean up before adding text
    textGroup.selectAll('text.elm-text').remove();
    textGroup
      .selectAll('.elm-text')
      .data((d) =>
        (d.textList || []).map((textEl) => {
          return { textElementInfo: textEl, stripElement: d as TStripElement };
        }),
      )
      .join('text')
      .classed('elm-text', true)
      .style('pointer-events', 'none')
      .text((d) => d.textElementInfo.value)
      .attr('font-size', (d) => d.textElementInfo.fontSizeInPx ?? 14)
      .attr('font-weight', 'bold')
      .attr('fill', (d) => (d.stripElement.isStripHighlighted ? HIGHLIGHTED_STRIP.TEXT_COLOR : d.textElementInfo.color))
      .attr('text-anchor', (d) =>
        d.textElementInfo.justify === 'center' ? 'middle' : d.textElementInfo.justify === 'right' ? 'end' : 'start',
      )
      .attr('x', (d) =>
        d.textElementInfo.justify === 'center' ? '50%' : d.textElementInfo.justify === 'right' ? '100%' : 4,
      )
      .attr('dominant-baseline', (d) => {
        const align = d.textElementInfo.align ?? 'center';
        if (align === 'center') return 'central';
        if (align === 'bottom') return 'baseline';
        return 'text-before-edge';
      })
      .attr('y', (d) => {
        const align = d.textElementInfo.align ?? 'center';
        const padding = 4;
        const stackHeightInPx = drawModel.layout.stackHeights[d.stripElement.stackCount];
        const height = ((d.stripElement.heightInPercent ?? 100) * stackHeightInPx) / 100;

        if (align === 'top') return padding;
        if (align === 'bottom') return height - padding;
        return height / 2;
      });

    // Clean up
    elements.selectAll('rect.elm-text-border').remove();
    elements
      .filter((d) => d.type === 'STRIP' && (!!d.textBorderColor || !!d.isStripHighlighted))
      .selectAll('.elm-text-border')
      .data((d) => [d] as Required<TStripElement>[])
      .join('rect')
      .classed('elm-text-border', true)
      .attr('x', 1)
      .attr('y', 1)
      .attr('width', 'calc(100% - 2px)')
      .attr('height', 'calc(100% - 2px)')
      .attr('font-weight', 'bold')
      .attr('stroke', (d) => (d.isStripHighlighted ? HIGHLIGHTED_STRIP.BORDER_COLOR : d.textBorderColor))
      .attr('stroke-width', 2)
      .attr('stroke-dasharray', (d) => (d.isStripHighlighted ? '5, 5' : '0'))
      .attr('fill', 'transparent')
      .attr('rx', '4')
      .attr('ry', '4');

    // Clean up
    elements.selectAll('circle.elm-circle').remove();
    elements
      .filter((d) => d.type === 'CIRCLE')
      .selectAll('.elm-circle')
      .data((d) => [d] as TCircleElements[])
      .join('circle')
      .classed('elm-circle', true)
      .attr('width', '100%')
      .attr('cx', 0)
      .attr('cy', 0)
      .attr('r', drawModel.timelineModel.boardView.boardViewType === 'MONTHLY' ? 2 : 5)
      .attr(
        'transform',
        (d) => `translate(${drawModel.layout.widthOfDay / 2}, ${drawModel.layout.stackHeights[d.stackCount] / 2})`,
      )
      .attr('fill', (d) => d.color)
      .attr('stroke', 'black')
      .attr('stroke-width', '2');

    // Clean up before adding icons
    elements.selectAll('.icon-group').remove();
    const iconGroup = elements
      .filter((d) => d.type === 'STRIP' && d.iconList && d.iconList.length > 0)
      .selectAll('.icon-group')
      .data((d) => [d] as TStripElement[])
      .join('g')
      .classed('icon-group', true);

    iconGroup.selectAll('.icon-svg').remove();
    iconGroup
      .selectAll('.icon-svg')
      .data((d) => {
        let offsetFromRight = 0;

        return d.iconList.map((icon) => {
          const width = getIconWidth(icon);
          offsetFromRight += width + STRIP_ICON.GAP;
          return { iconElementInfo: icon, stripElement: d as TStripElement, width, offsetFromRight };
        });
      })
      .join('svg')
      .each(function (d) {
        const icon = d.iconElementInfo;
        const iconSvg = d3.select(this);

        if (icon.background) {
          iconSvg
            .append('rect')
            .attr('width', '100%')
            .attr('height', '100%')
            .attr('rx', STRIP_ICON.BORDER_RADIUS)
            .attr('fill', icon.background);
        }

        if (icon.text) {
          iconSvg
            .append('text')
            .attr('x', STRIP_ICON.PADDING_LEFT)
            .attr('y', STRIP_ICON.SIZE / 2)
            .attr('dominant-baseline', 'central')
            .attr('font-size', STRIP_ICON.FONT_SIZE)
            .attr('font-weight', 'bold')
            .attr('fill', icon.svgColor || STRIP_ICON.DEFAULT_ICON_COLOR)
            .text(icon.text);
        }

        const glyph = iconSvg
          .append('svg')
          .attr('x', d.width - STRIP_ICON.SIZE)
          .attr('width', STRIP_ICON.SIZE)
          .attr('height', STRIP_ICON.SIZE)
          .attr('viewBox', `0 0 ${STRIP_ICON.GLYPH_VIEWBOX} ${STRIP_ICON.GLYPH_VIEWBOX}`)
          .append('path')
          .attr('d', icon.svgPath)
          .attr('fill', icon.svgColor || STRIP_ICON.DEFAULT_ICON_COLOR);

        if (icon.fillRule) {
          glyph.attr('fill-rule', icon.fillRule);
        }
      })
      .classed('icon-svg', true)
      .attr('width', (d) => d.width)
      .attr('height', STRIP_ICON.SIZE)
      .attr('x', (d) => `calc(100% - ${d.offsetFromRight}px)`)
      .attr('y', '3.5px')
      .attr('fill', 'none')
      .on('click.icon_event', (event$: Event, el) => {
        if (el.iconElementInfo.clickAction) {
          event$.stopPropagation();
          const iconSvgElement = document.querySelector('.icon-svg');
          const closestSvgElement = iconSvgElement?.closest('svg');

          if (closestSvgElement) {
            const parentSvgElementData = d3.select(closestSvgElement).datum();

            // Get the parent's parent SVG element
            const ancestorSvgElement = closestSvgElement.closest('svg');

            if (ancestorSvgElement) {
              const entityGroupData = d3.select(ancestorSvgElement).datum();

              const emitVal = {
                type: 'STRIP',
                action: el.iconElementInfo.clickAction,
                event,
                elementData: el.stripElement.data,
                entityData: parentSvgElementData,
                entityGroupData: entityGroupData,
              };
              stripEventCb(emitVal as TEntityActions);
            }
          }
        }
      });
  },
};
